/**
 * LYT Communications - Payment Tracker
 * Version: 1.0.0
 * Created: 2026-03-04
 * Route: #payment-tracker
 *
 * Unified admin command center for all payment activity.
 * Shows production submissions (Track 1) and payment invoices (Track 2)
 * with approval/payment logging and summary stats.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, DollarSign, Clock, CheckCircle, CreditCard, FileText,
  Send, Filter, ChevronDown, ChevronUp, Loader, X, Check,
  AlertTriangle, Sun, Moon, TrendingUp
} from 'lucide-react';
import {
  loadProductionSubmissions, loadPaymentInvoices,
  updateSubmissionField, updatePaymentInvoiceField,
  markSegmentsBilled, markDevicesBilled,
  createProductionSubmission, loadFullProject,
  loadInvoices, approveInvoice, updateInvoiceField,
  fetchFullRates
} from '../services/mapService';

function PaymentTracker({ darkMode, user, setCurrentPage, projectId }) {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Contractor invoices state
  const [contractorInvoices, setContractorInvoices] = useState([]);
  const [rejectModal, setRejectModal] = useState(null); // { invoice }
  const [rejectReason, setRejectReason] = useState('');

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState(null); // { id, type: 'submission'|'invoice' }
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentInvNum, setPaymentInvNum] = useState('');

  // Approval modal
  const [approvalModal, setApprovalModal] = useState(null); // { id, type }

  // Manual payment entry modal
  const [manualModal, setManualModal] = useState(false);
  const [manualSegments, setManualSegments] = useState([]);
  const [manualSegLoading, setManualSegLoading] = useState(false);
  const [manualSelected, setManualSelected] = useState(new Set()); // "segId:phase"
  const [manualPhaseFilter, setManualPhaseFilter] = useState('all'); // all|boring|pulling
  const [manualStatusFilter, setManualStatusFilter] = useState('unpaid'); // all|unpaid
  const [manualSearch, setManualSearch] = useState('');
  const [manualForm, setManualForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    reference: '',
    invoiceNum: '',
    notes: '',
  });

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const border = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const muted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Client billing rates — fetched from server (key-gated), never in the bundle
  const [fullRateInfo, setFullRateInfo] = useState(null);
  useEffect(() => {
    let live = true;
    fetchFullRates('vexus-la-tx-2026')
      .then(d => { if (live && d) setFullRateInfo(d); })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, []);
  const vexusRates = fullRateInfo?.rates || {};

  const loadData = useCallback(async () => {
    try {
      const [subs, invs, cInvs] = await Promise.all([
        projectId ? loadProductionSubmissions(projectId) : [],
        projectId ? loadPaymentInvoices(projectId) : loadPaymentInvoices(),
        loadInvoices(null),
      ]);
      setSubmissions(subs || []);
      setInvoices(invs || []);
      setContractorInvoices(cInvs || []);
    } catch (err) {
      console.error('Failed to load payment data:', err);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Unified list of all payment items
  const allItems = useMemo(() => {
    const items = [
      ...submissions.map(s => ({
        ...s,
        itemType: 'production',
        id: s.submission_id,
        reference: `SUB-${(s.submission_date || '').slice(5)}`,
        amount: parseFloat(s.subtotal) || 0,
        date: s.submission_date,
        paidAmount: parseFloat(s.payment_amount) || 0,
      })),
      ...invoices.map(i => ({
        ...i,
        itemType: 'invoice',
        id: i.invoice_id,
        reference: i.invoice_number || i.invoice_id,
        amount: parseFloat(i.subtotal) || 0,
        date: i.created_at?.slice(0, 10),
        paidAmount: parseFloat(i.payment_amount) || 0,
      })),
    ];
    return items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [submissions, invoices]);

  // Filter by tab
  const filteredItems = useMemo(() => {
    switch (activeTab) {
      case 'production': return allItems.filter(i => i.itemType === 'production');
      case 'invoices': return allItems.filter(i => i.itemType === 'invoice');
      case 'pending-approval': return allItems.filter(i => i.status === 'Submitted');
      case 'pending-payment': return allItems.filter(i => i.status === 'Approved');
      default: return allItems;
    }
  }, [allItems, activeTab]);

  // Summary stats
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const thisYear = `${now.getFullYear()}`;

    let outstanding = 0, awaitingApproval = 0, paidThisMonth = 0, paidThisYear = 0;

    allItems.forEach(item => {
      if (item.status === 'Submitted') { outstanding += item.amount; awaitingApproval += item.amount; }
      else if (item.status === 'Approved') { outstanding += item.amount; }
      if (item.status === 'Paid') {
        const paidDate = item.payment_received_at || item.paid_at || '';
        if (paidDate.startsWith(thisMonth)) paidThisMonth += item.paidAmount;
        if (paidDate.startsWith(thisYear)) paidThisYear += item.paidAmount;
      }
    });

    return { outstanding, awaitingApproval, paidThisMonth, paidThisYear };
  }, [allItems]);

  // Actions
  const handleLogApproval = async () => {
    if (!approvalModal) return;
    setActionLoading(true);
    try {
      const { id, type } = approvalModal;
      if (type === 'submission') {
        await updateSubmissionField(id, 'status', 'Approved');
        await updateSubmissionField(id, 'client_approved_at', new Date().toISOString());
        const sub = submissions.find(s => s.submission_id === id);
        if (sub?.line_items_json) {
          const items = JSON.parse(sub.line_items_json);
          const boringIds = items.filter(li => li.phase === 'boring').map(li => li.segment_id);
          const pullingIds = items.filter(li => li.phase === 'pulling').map(li => li.segment_id);
          if (boringIds.length) await markSegmentsBilled(boringIds, 'boring', 'Approved', id);
          if (pullingIds.length) await markSegmentsBilled(pullingIds, 'pulling', 'Approved', id);
        }
      } else {
        await updatePaymentInvoiceField(id, 'status', 'Approved');
        await updatePaymentInvoiceField(id, 'client_approved_at', new Date().toISOString());
        const inv = invoices.find(i => i.invoice_id === id);
        if (inv?.device_ids) {
          const devIds = JSON.parse(inv.device_ids);
          if (devIds.length) await markDevicesBilled(devIds, 'Approved', id, 'invoice');
        }
      }
      await loadData();
      setApprovalModal(null);
      showToast('Approval logged');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  const openManualModal = async () => {
    setManualModal(true);
    setManualSegLoading(true);
    setManualSelected(new Set());
    setManualSearch('');
    setManualPhaseFilter('all');
    setManualStatusFilter('unpaid');
    try {
      if (projectId) {
        const data = await loadFullProject(projectId);
        setManualSegments(data.segments || []);
      }
    } catch (err) {
      console.error('Failed to load segments:', err);
    }
    setManualSegLoading(false);
  };

  // Build selectable items from segments (each segment can have boring + pulling as separate line items)
  const manualItems = useMemo(() => {
    const items = [];
    manualSegments.forEach(seg => {
      const id = seg.segment_id;
      const street = seg.street || seg.section || '';
      const footage = parseFloat(seg.footage) || 0;
      // Boring
      if (seg.boring_status) {
        const billed = seg.boring_billing_status || 'Not Billed';
        const code = seg.duct_count === 2 ? 'UG2' : seg.duct_count === 3 ? 'UG3' : (seg.bore_type === '2" HDPE' ? 'UG23' : 'UG1');
        // (bug fix: previously assigned the whole rate object, producing NaN totals)
        const rate = vexusRates?.[code]?.vexus || 0;
        items.push({
          key: `${id}:boring`,
          segId: id,
          phase: 'boring',
          street,
          footage,
          code,
          rate,
          total: footage * rate,
          billingStatus: billed,
          workStatus: seg.boring_status,
          assignedTo: seg.boring_assigned_to || '',
        });
      }
      // Pulling
      if (seg.pulling_status) {
        const billed = seg.pulling_billing_status || 'Not Billed';
        const code = 'UG4';
        const rate = vexusRates?.[code]?.vexus || 0;
        items.push({
          key: `${id}:pulling`,
          segId: id,
          phase: 'pulling',
          street,
          footage,
          code,
          rate,
          total: footage * rate,
          billingStatus: billed,
          workStatus: seg.pulling_status,
          assignedTo: seg.pulling_assigned_to || '',
        });
      }
    });
    return items;
  }, [manualSegments, vexusRates]);

  const filteredManualItems = useMemo(() => {
    return manualItems.filter(item => {
      if (manualPhaseFilter !== 'all' && item.phase !== manualPhaseFilter) return false;
      if (manualStatusFilter === 'unpaid' && item.billingStatus === 'Paid') return false;
      if (manualSearch) {
        const q = manualSearch.toLowerCase();
        if (!item.segId.toLowerCase().includes(q) && !item.street.toLowerCase().includes(q) && !item.assignedTo.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [manualItems, manualPhaseFilter, manualStatusFilter, manualSearch]);

  const manualTotal = useMemo(() => {
    let sum = 0;
    manualSelected.forEach(key => {
      const item = manualItems.find(i => i.key === key);
      if (item) sum += item.total;
    });
    return sum;
  }, [manualSelected, manualItems]);

  const toggleManualItem = (key) => {
    setManualSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const allVisible = filteredManualItems.every(i => manualSelected.has(i.key));
    setManualSelected(prev => {
      const next = new Set(prev);
      filteredManualItems.forEach(i => { if (allVisible) next.delete(i.key); else next.add(i.key); });
      return next;
    });
  };

  const handleManualEntry = async () => {
    if (manualSelected.size === 0) return;
    setActionLoading(true);
    try {
      // Group selected items by phase
      const boringIds = [];
      const pullingIds = [];
      const lineItems = [];
      manualSelected.forEach(key => {
        const item = manualItems.find(i => i.key === key);
        if (!item) return;
        if (item.phase === 'boring') boringIds.push(item.segId);
        else pullingIds.push(item.segId);
        lineItems.push({ code: item.code, description: `${item.street} — ${item.segId}`, qty: item.footage, rate: item.rate, total: item.total, phase: item.phase, segment_id: item.segId });
      });

      const totalAmount = lineItems.reduce((s, li) => s + li.total, 0).toFixed(2);

      // Create submission record
      const subId = await createProductionSubmission({
        project_id: projectId || 'MANUAL',
        submission_date: manualForm.date,
        subtotal: totalAmount,
        status: 'Paid',
        notes: manualForm.notes ? `[Manual Entry] ${manualForm.notes}` : '[Manual Entry] Backfill payment',
        submitted_by: user?.email || 'admin',
        submitted_at: new Date().toISOString(),
        client_approved_at: new Date().toISOString(),
        payment_received_at: manualForm.date + 'T00:00:00Z',
        payment_amount: totalAmount,
        payment_reference: manualForm.reference,
        payment_invoice_number: manualForm.invoiceNum,
        line_items_json: JSON.stringify(lineItems),
      });

      if (subId) {
        // Mark segments as Paid
        if (boringIds.length) await markSegmentsBilled(boringIds, 'boring', 'Paid', subId);
        if (pullingIds.length) await markSegmentsBilled(pullingIds, 'pulling', 'Paid', subId);

        await loadData();
        setManualModal(false);
        setManualSelected(new Set());
        setManualForm({ date: new Date().toISOString().slice(0, 10), reference: '', invoiceNum: '', notes: '' });
        showToast(`${manualSelected.size} segments marked as paid — ${fmt(totalAmount)}`);
      }
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  const handleLogPayment = async () => {
    if (!paymentModal || !paymentAmount) return;
    setActionLoading(true);
    try {
      const { id, type } = paymentModal;
      if (type === 'submission') {
        await updateSubmissionField(id, 'status', 'Paid');
        await updateSubmissionField(id, 'payment_received_at', new Date().toISOString());
        await updateSubmissionField(id, 'payment_amount', paymentAmount);
        if (paymentRef) await updateSubmissionField(id, 'payment_reference', paymentRef);
        if (paymentInvNum) await updateSubmissionField(id, 'payment_invoice_number', paymentInvNum);
        const sub = submissions.find(s => s.submission_id === id);
        if (sub?.line_items_json) {
          const items = JSON.parse(sub.line_items_json);
          const boringIds = items.filter(li => li.phase === 'boring').map(li => li.segment_id);
          const pullingIds = items.filter(li => li.phase === 'pulling').map(li => li.segment_id);
          if (boringIds.length) await markSegmentsBilled(boringIds, 'boring', 'Paid', id);
          if (pullingIds.length) await markSegmentsBilled(pullingIds, 'pulling', 'Paid', id);
        }
      } else {
        await updatePaymentInvoiceField(id, 'status', 'Paid');
        await updatePaymentInvoiceField(id, 'payment_received_at', new Date().toISOString());
        await updatePaymentInvoiceField(id, 'payment_amount', paymentAmount);
        if (paymentRef) await updatePaymentInvoiceField(id, 'payment_reference', paymentRef);
        const inv = invoices.find(i => i.invoice_id === id);
        if (inv?.device_ids) {
          const devIds = JSON.parse(inv.device_ids);
          if (devIds.length) await markDevicesBilled(devIds, 'Paid', id, 'invoice');
        }
      }
      await loadData();
      setPaymentModal(null);
      setPaymentAmount('');
      setPaymentRef('');
      setPaymentInvNum('');
      showToast('Payment recorded');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  // Contractor invoice approve (WORKFLOW Step 135)
  const handleApproveContractorInvoice = async (inv) => {
    setActionLoading(true);
    try {
      await approveInvoice(inv.invoice_id, user?.email || 'admin');
      try {
        const { notifyContractorInvoiceResult } = await import('../services/notificationService');
        if (inv.contractor_email) {
          await notifyContractorInvoiceResult(inv.contractor_email, inv.invoice_number || inv.invoice_id, true, null);
        }
      } catch (err) { console.warn('[PaymentTracker] Approve notification failed:', err); }
      await loadData();
      showToast('Contractor invoice approved');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  // Contractor invoice reject (WORKFLOW Step 127)
  const handleRejectContractorInvoice = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      const inv = rejectModal;
      await updateInvoiceField(inv.invoice_id, 'status', 'Rejected');
      await updateInvoiceField(inv.invoice_id, 'rejected_reason', rejectReason.trim());
      try {
        const { notifyContractorInvoiceResult } = await import('../services/notificationService');
        if (inv.contractor_email) {
          await notifyContractorInvoiceResult(inv.contractor_email, inv.invoice_number || inv.invoice_id, false, rejectReason.trim());
        }
      } catch (err) { console.warn('[PaymentTracker] Reject notification failed:', err); }
      await loadData();
      setRejectModal(null);
      setRejectReason('');
      showToast('Contractor invoice rejected');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  const pendingContractorCount = contractorInvoices.filter(i => i.status === 'Submitted').length;

  const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} color={accent} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14, backgroundColor: toast.type === 'error' ? '#dc3545' : '#4CAF50', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setCurrentPage('admin-dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}><ArrowLeft size={20} /></button>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Payment Tracker</h1>
            <div style={{ fontSize: 13, color: muted }}>Track production submissions and invoice payments</div>
          </div>
        </div>
        <button onClick={openManualModal} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: accent, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
          <DollarSign size={14} /> Mark Segments Paid
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '16px 20px' }}>
        {[
          { label: 'Outstanding', value: fmt(stats.outstanding), icon: <Clock size={18} />, color: '#FFB800' },
          { label: 'Awaiting Approval', value: fmt(stats.awaitingApproval), icon: <AlertTriangle size={18} />, color: '#FF9800' },
          { label: 'Paid This Month', value: fmt(stats.paidThisMonth), icon: <CreditCard size={18} />, color: '#4CAF50' },
          { label: 'Paid This Year', value: fmt(stats.paidThisYear), icon: <TrendingUp size={18} />, color: '#2196F3' },
        ].map((card, i) => (
          <div key={i} style={{ backgroundColor: cardBg, borderRadius: 10, padding: 16, border: `1px solid ${border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ color: card.color }}>{card.icon}</div>
              <span style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase' }}>{card.label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${border}`, paddingLeft: 20 }}>
        {[
          { id: 'all', label: `All (${allItems.length})` },
          { id: 'production', label: `Production (${submissions.length})` },
          { id: 'invoices', label: `Invoices (${invoices.length})` },
          { id: 'pending-approval', label: `Pending Approval (${allItems.filter(i => i.status === 'Submitted').length})` },
          { id: 'pending-payment', label: `Pending Payment (${allItems.filter(i => i.status === 'Approved').length})` },
          { id: 'contractor-invoices', label: `Contractor Inv (${pendingContractorCount})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '12px 16px', background: 'none', border: 'none',
            borderBottom: activeTab === tab.id ? `2px solid ${accent}` : '2px solid transparent',
            color: activeTab === tab.id ? accent : muted, fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contractor Invoices Tab */}
      {activeTab === 'contractor-invoices' && (
        <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
          {contractorInvoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: muted }}>
              <FileText size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No Contractor Invoices</div>
              <div style={{ fontSize: 13 }}>No contractor invoices have been submitted yet.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {contractorInvoices.map(inv => {
                const isExpanded = expandedItem === inv.invoice_id;
                let lineItems = [];
                try { lineItems = inv.line_items_json ? JSON.parse(inv.line_items_json) : []; } catch (e) {}
                const subtotal = parseFloat(inv.subtotal) || 0;
                const retainage = parseFloat(inv.retainage_amount) || 0;
                const net = parseFloat(inv.net_amount) || 0;

                return (
                  <div key={inv.invoice_id} style={{ backgroundColor: cardBg, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
                    <div onClick={() => setExpandedItem(isExpanded ? null : inv.invoice_id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FF980015' }}>
                          <FileText size={16} color="#FF9800" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoice_number || inv.invoice_id}</span>
                            <BillingBadge status={inv.status} />
                          </div>
                          <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>
                            {inv.contractor_company || 'Unknown'} — {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, fontSize: 16 }}>{fmt(net)}</div>
                          <div style={{ fontSize: 11, color: muted }}>of {fmt(subtotal)}</div>
                        </div>
                        {isExpanded ? <ChevronUp size={16} color={muted} /> : <ChevronDown size={16} color={muted} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${border}` }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, padding: '12px 0' }}>
                          <InfoItem label="Contractor" value={inv.contractor_company || '—'} />
                          <InfoItem label="Email" value={inv.contractor_email || '—'} />
                          <InfoItem label="Project" value={inv.project_id || '—'} />
                          <InfoItem label="Submitted" value={inv.submitted_at ? new Date(inv.submitted_at).toLocaleString() : '—'} />
                          {inv.approved_by && <InfoItem label="Approved By" value={inv.approved_by} />}
                          {inv.approved_at && <InfoItem label="Approved At" value={new Date(inv.approved_at).toLocaleString()} />}
                          {inv.rejected_reason && <InfoItem label="Reject Reason" value={inv.rejected_reason} />}
                          {inv.notes && <InfoItem label="Notes" value={inv.notes} />}
                        </div>

                        {lineItems.length > 0 && (
                          <div style={{ overflowX: 'auto', marginTop: 8 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9' }}>
                                  {['Code', 'Description', 'Qty', 'Rate', 'Total'].map(h => (
                                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${border}` }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {lineItems.map((li, i) => (
                                  <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{li.code}</td>
                                    <td style={{ padding: '8px 10px' }}>{li.description}</td>
                                    <td style={{ padding: '8px 10px' }}>{li.qty}</td>
                                    <td style={{ padding: '8px 10px' }}>${parseFloat(li.rate || 0).toFixed(2)}</td>
                                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>${parseFloat(li.total || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 8, padding: '8px 0', borderTop: `1px solid ${border}` }}>
                          <div style={{ fontSize: 13 }}>Subtotal: <strong>{fmt(subtotal)}</strong></div>
                          <div style={{ fontSize: 13, color: '#FFB800' }}>Retainage: <strong>-{fmt(retainage)}</strong></div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>Net: <strong style={{ color: '#4CAF50' }}>{fmt(net)}</strong></div>
                        </div>

                        {inv.status === 'Submitted' && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button onClick={() => handleApproveContractorInvoice(inv)} disabled={actionLoading} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                              <Check size={14} /> Approve
                            </button>
                            <button onClick={() => { setRejectModal(inv); setRejectReason(''); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                              <X size={14} /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Items List */}
      {activeTab !== 'contractor-invoices' && <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: muted }}>
            <DollarSign size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No Items</div>
            <div style={{ fontSize: 13 }}>No payment items match this filter.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredItems.map(item => {
              const isExpanded = expandedItem === item.id;
              const lineItems = item.line_items_json ? JSON.parse(item.line_items_json) : [];

              return (
                <div key={item.id} style={{ backgroundColor: cardBg, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
                  {/* Row */}
                  <div onClick={() => setExpandedItem(isExpanded ? null : item.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: item.itemType === 'production' ? '#FFB80015' : '#9C27B015' }}>
                        {item.itemType === 'production' ? <Send size={16} color="#FFB800" /> : <FileText size={16} color="#9C27B0" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{item.reference}</span>
                          <BillingBadge status={item.status} />
                          <span style={{ fontSize: 11, color: muted, backgroundColor: `${muted}15`, padding: '2px 6px', borderRadius: 4 }}>
                            {item.itemType === 'production' ? 'Production' : item.invoice_type || 'Invoice'}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>
                          {item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          {item.submitted_to_email && ` — sent to ${item.submitted_to_email}`}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>{fmt(item.amount)}</div>
                        {item.status === 'Paid' && <div style={{ fontSize: 12, color: '#4CAF50' }}>Paid: {fmt(item.paidAmount)}</div>}
                      </div>
                      {isExpanded ? <ChevronUp size={16} color={muted} /> : <ChevronDown size={16} color={muted} />}
                    </div>
                  </div>

                  {/* Expanded */}
                  {isExpanded && (
                    <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${border}` }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, padding: '12px 0' }}>
                        <InfoItem label="Submitted" value={item.submitted_at ? new Date(item.submitted_at).toLocaleString() : '—'} />
                        <InfoItem label="Submitted By" value={item.submitted_by || '—'} />
                        <InfoItem label="Client Approved" value={(item.client_approved_at || item.metronet_approved_at) ? new Date(item.client_approved_at || item.metronet_approved_at).toLocaleString() : '—'} />
                        <InfoItem label="Payment Date" value={item.payment_received_at ? new Date(item.payment_received_at).toLocaleString() : '—'} />
                        <InfoItem label="Payment Ref" value={item.payment_reference || '—'} />
                        {item.itemType === 'submission' && <InfoItem label="Client Inv #" value={item.payment_invoice_number || '—'} />}
                        {item.notes && <InfoItem label="Notes" value={item.notes} />}
                      </div>

                      {lineItems.length > 0 && (
                        <div style={{ overflowX: 'auto', marginTop: 8 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9' }}>
                                {['Code', 'Description', 'Qty', 'Rate', 'Total'].map(h => (
                                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${border}` }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {lineItems.map((li, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{li.code}</td>
                                  <td style={{ padding: '8px 10px' }}>{li.description}</td>
                                  <td style={{ padding: '8px 10px' }}>{li.qty}</td>
                                  <td style={{ padding: '8px 10px' }}>${parseFloat(li.rate).toFixed(2)}</td>
                                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>${parseFloat(li.total).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        {item.status === 'Submitted' && (
                          <button onClick={() => setApprovalModal({ id: item.id, type: item.itemType === 'production' ? 'submission' : 'invoice' })} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                            <Check size={14} /> Log Approval
                          </button>
                        )}
                        {['Submitted', 'Approved'].includes(item.status) && (
                          <button onClick={() => { setPaymentModal({ id: item.id, type: item.itemType === 'production' ? 'submission' : 'invoice' }); setPaymentAmount(String(item.amount)); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                            <CreditCard size={14} /> Log Payment
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {/* Reject Contractor Invoice Modal */}
      {rejectModal && (
        <Modal onClose={() => { setRejectModal(null); setRejectReason(''); }} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Reject Invoice: {rejectModal.invoice_number || rejectModal.invoice_id}</h3>
          <p style={{ fontSize: 14, color: muted, margin: '0 0 12px' }}>Provide a reason for rejection. The contractor will be notified by email.</p>
          <textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Reason for rejection (required)"
            rows={3}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={() => { setRejectModal(null); setRejectReason(''); }} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={handleRejectContractorInvoice} disabled={actionLoading || !rejectReason.trim()} style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: !rejectReason.trim() ? 0.5 : 1 }}>
              {actionLoading ? 'Saving...' : 'Reject Invoice'}
            </button>
          </div>
        </Modal>
      )}

      {/* Approval Modal */}
      {approvalModal && (
        <Modal onClose={() => setApprovalModal(null)} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Confirm Client Approval</h3>
          <p style={{ fontSize: 14, color: muted, margin: '0 0 20px' }}>Mark this as approved by client? Segment/device billing statuses will be updated.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setApprovalModal(null)} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={handleLogApproval} disabled={actionLoading} style={{ padding: '10px 20px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {actionLoading ? 'Saving...' : 'Confirm Approval'}
            </button>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <Modal onClose={() => { setPaymentModal(null); setPaymentAmount(''); setPaymentRef(''); setPaymentInvNum(''); }} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Record Payment Received</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Payment Amount *</label>
              <input type="number" step="0.01" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Payment Reference</label>
              <input type="text" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="CHK-12345 or ACH ref" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            {paymentModal?.type === 'submission' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Client Invoice #</label>
                <input type="text" value={paymentInvNum} onChange={e => setPaymentInvNum(e.target.value)} placeholder="MET-INV-2026-001" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setPaymentModal(null); setPaymentAmount(''); setPaymentRef(''); setPaymentInvNum(''); }} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={handleLogPayment} disabled={actionLoading || !paymentAmount} style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {actionLoading ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </Modal>
      )}

      {/* Mark Segments Paid Modal */}
      {manualModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setManualModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: darkMode ? '#112240' : '#ffffff', borderRadius: 12, padding: 24, maxWidth: 800, width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', position: 'relative', color: text }}>
            <button onClick={() => setManualModal(false)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: muted }}><X size={18} /></button>

            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Mark Segments as Paid</h3>
            <p style={{ fontSize: 13, color: muted, margin: '0 0 16px' }}>Select segments the client has paid for. This marks them as Paid so you know it's safe to pay contractors.</p>

            {manualSegLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><Loader size={24} color={accent} style={{ animation: 'spin 1s linear infinite' }} /> Loading segments...</div>
            ) : (
              <>
                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="text" value={manualSearch} onChange={e => setManualSearch(e.target.value)} placeholder="Search segment, street, contractor..." style={{ flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                  {['all', 'boring', 'pulling'].map(f => (
                    <button key={f} onClick={() => setManualPhaseFilter(f)} style={{ padding: '6px 12px', borderRadius: 6, border: manualPhaseFilter === f ? 'none' : `1px solid ${border}`, backgroundColor: manualPhaseFilter === f ? accent : 'transparent', color: manualPhaseFilter === f ? '#fff' : muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>{f}</button>
                  ))}
                  <button onClick={() => setManualStatusFilter(f => f === 'unpaid' ? 'all' : 'unpaid')} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`, backgroundColor: manualStatusFilter === 'unpaid' ? '#FFB80020' : 'transparent', color: manualStatusFilter === 'unpaid' ? '#FFB800' : muted, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {manualStatusFilter === 'unpaid' ? 'Hide Paid' : 'Show All'}
                  </button>
                </div>

                {/* Select all + count */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: muted, cursor: 'pointer' }}>
                    <input type="checkbox" checked={filteredManualItems.length > 0 && filteredManualItems.every(i => manualSelected.has(i.key))} onChange={toggleAllVisible} />
                    Select all visible ({filteredManualItems.length})
                  </label>
                  <span style={{ fontSize: 13, fontWeight: 600, color: manualSelected.size > 0 ? '#4CAF50' : muted }}>
                    {manualSelected.size} selected — {fmt(manualTotal)}
                  </span>
                </div>

                {/* Segment list */}
                <div style={{ flex: 1, overflowY: 'auto', maxHeight: 340, border: `1px solid ${border}`, borderRadius: 8 }}>
                  {filteredManualItems.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: muted, fontSize: 13 }}>No segments match filters</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '8px 10px', width: 30 }}></th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Segment</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Street</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Phase</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Footage</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Billing</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredManualItems.map(item => (
                          <tr key={item.key} onClick={() => toggleManualItem(item.key)} style={{ borderBottom: `1px solid ${border}`, cursor: 'pointer', backgroundColor: manualSelected.has(item.key) ? (darkMode ? '#4CAF5015' : '#4CAF5010') : 'transparent' }}>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                              <input type="checkbox" checked={manualSelected.has(item.key)} readOnly />
                            </td>
                            <td style={{ padding: '6px 10px', fontWeight: 600, fontFamily: 'monospace', fontSize: 11 }}>{item.segId}</td>
                            <td style={{ padding: '6px 10px' }}>{item.street}</td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: item.phase === 'boring' ? '#FF980020' : '#2196F320', color: item.phase === 'boring' ? '#FF9800' : '#2196F3', textTransform: 'uppercase' }}>{item.phase}</span>
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{item.footage} LF</td>
                            <td style={{ padding: '6px 10px' }}><BillingBadge status={item.workStatus} /></td>
                            <td style={{ padding: '6px 10px' }}><BillingBadge status={item.billingStatus} /></td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Payment details */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Payment Date</label>
                    <input type="date" value={manualForm.date} onChange={e => setManualForm(f => ({ ...f, date: e.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Check / ACH Ref</label>
                    <input type="text" value={manualForm.reference} onChange={e => setManualForm(f => ({ ...f, reference: e.target.value }))} placeholder="CHK-12345" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Client Invoice #</label>
                    <input type="text" value={manualForm.invoiceNum} onChange={e => setManualForm(f => ({ ...f, invoiceNum: e.target.value }))} placeholder="MET-INV-001" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Notes (optional)</label>
                  <input type="text" value={manualForm.notes} onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any extra details" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => setManualModal(false)} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                  <button onClick={handleManualEntry} disabled={actionLoading || manualSelected.size === 0} style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: manualSelected.size === 0 ? 0.5 : 1 }}>
                    {actionLoading ? 'Saving...' : `Mark ${manualSelected.size} Segment${manualSelected.size !== 1 ? 's' : ''} Paid — ${fmt(manualTotal)}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function BillingBadge({ status }) {
  const colors = {
    'Draft': { bg: '#9E9E9E20', text: '#9E9E9E' },
    'Submitted': { bg: '#FFB80020', text: '#FFB800' },
    'Approved': { bg: '#2196F320', text: '#2196F3' },
    'Paid': { bg: '#4CAF5020', text: '#4CAF50' },
  };
  const c = colors[status] || colors['Draft'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, backgroundColor: c.bg, color: c.text, fontSize: 11, fontWeight: 700 }}>
      <span style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: c.text }} />
      {status}
    </span>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#8892b0', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value}</div>
    </div>
  );
}

function Modal({ children, onClose, darkMode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: darkMode ? '#112240' : '#ffffff', borderRadius: 12, padding: 24, maxWidth: 480, width: '90%', position: 'relative', color: darkMode ? '#ffffff' : '#1e293b' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? '#8892b0' : '#64748b' }}><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}

export default PaymentTracker;
